import type { EvaluationTypeValue } from '@/constants/cycle-evaluation/CycleEvaluation';
import type { CycleInstanceSteps } from '@/constants/cycle-evaluation/CycleInstanceSteps';

import {
    useCreateCycleInstance,
    useGetCycleInstanceAsync,
} from '@/api/cycle-evaluation-services/cycle-instance';
import { useUpdateReviewAudience } from '@/api/review-services/review';
import { useNavigate } from '@/hooks';
import { useError } from '@/hooks/utils/useError';
import { getReviewFormRouteName } from '@/navigation/utils';
import { getStepPath } from '@/utils/cycleInstance';

interface StartEvaluationParams {
    evaluationType: EvaluationTypeValue;
    /** Where the reader lands once the employee is on the audience; it differs per evaluation. */
    firstStep: CycleInstanceSteps;
}

/**
 * Opens a new evaluation on one employee: the cycle instance is created, the employee is put on
 * its review audience, and the reader is taken to the first step that still needs them.
 *
 * The self-evaluation and the 360 start the same way and differ only in the type they create and
 * the step they land on, so the sequence is written once. `isCreating` covers the three calls, not
 * just the first: the button stays busy until the reader is actually somewhere else.
 */
export const useStartEvaluation = ({ evaluationType, firstStep }: StartEvaluationParams) => {
    const navigate = useNavigate();
    const { handleError } = useError();
    const { mutateAsync: createCycleInstance, isPending: isCreatingInstance } =
        useCreateCycleInstance();
    const { mutateAsync: getCycleInstance, isPending: isFetchingInstance } =
        useGetCycleInstanceAsync();
    const { mutateAsync: updateReviewAudience, isPending: isUpdatingAudience } =
        useUpdateReviewAudience();

    const start = async (employeeId?: string) => {
        if (!employeeId) return;

        try {
            const createdInstance = await createCycleInstance(evaluationType);
            // The creation answers with the instance alone; the audience it has to be put on is
            // read back from the full one.
            const cycleInstance = await getCycleInstance(createdInstance.id);

            if (!cycleInstance.reviewAudience) {
                throw new Error('Review audience not found');
            }

            const reviewAudience = cycleInstance.reviewAudience;
            const now = new Date().toISOString();

            await updateReviewAudience({
                ...reviewAudience,
                reviewAudienceAssignments: [
                    {
                        id: '',
                        createdAt: now,
                        updatedAt: now,
                        reviewAudienceId: reviewAudience.id,
                        reviewAudience,
                        reviewEvaluatorAssignments: [],
                        assignedTo: employeeId,
                    },
                ],
            });

            navigate({
                path: `${getReviewFormRouteName(evaluationType)}/${getStepPath(firstStep)}/${cycleInstance.id}`,
            });
        } catch (error) {
            handleError({ error });
        }
    };

    return {
        start,
        isCreating: isCreatingInstance || isFetchingInstance || isUpdatingAudience,
    };
};
